import { useState } from 'react'
import { Send, Plus, History, ChevronDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useApiClient, type ApiClientRequest, type HistoryEntry } from '@/hooks/useApiClient'
import { ResponseViewer } from './ResponseViewer'

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

// ── History dialog ───────────────────────────────────────────────────────────

interface HistoryDialogProps {
  history: HistoryEntry[]
  onSelect: (entry: HistoryEntry) => void
  onClear: () => void
}

function HistoryDialog({ history, onSelect, onClear }: HistoryDialogProps) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  function handleSelect(entry: HistoryEntry) {
    onSelect(entry)
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" title={t('apiClient.historyButtonTitle')}>
          <History className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[70vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-6">
            <span>{t('apiClient.historyTitle')}</span>
            {history.length > 0 && (
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground h-auto py-1" onClick={onClear}>
                {t('apiClient.historyClearAll')}
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1">
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t('apiClient.historyEmpty')}</p>
          ) : (
            <div className="divide-y">
              {history.map((entry) => (
                <button
                  key={entry.id}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
                  onClick={() => handleSelect(entry)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-semibold text-muted-foreground w-14 shrink-0">
                      {entry.request.method}
                    </span>
                    <span className="text-xs font-mono truncate flex-1 text-foreground">
                      {entry.request.url}
                    </span>
                    <span className={cn(
                      'text-xs font-mono tabular-nums shrink-0',
                      entry.response.status < 300 ? 'text-green-600' :
                      entry.response.status < 400 ? 'text-blue-600' :
                      entry.response.status < 500 ? 'text-yellow-600' : 'text-red-600',
                    )}>
                      {entry.response.status}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 pl-16">
                    {formatTimestamp(entry.timestamp)} · {entry.response.latencyMs}ms
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Header row editor ────────────────────────────────────────────────────────

export interface HeaderRow { key: string; value: string }

interface HeaderEditorProps {
  rows: HeaderRow[]
  onChange: (rows: HeaderRow[]) => void
}

function HeaderEditor({ rows, onChange }: HeaderEditorProps) {
  const { t } = useTranslation()

  function addRow() {
    onChange([...rows, { key: '', value: '' }])
  }

  function updateRow(i: number, field: 'key' | 'value', val: string) {
    const next = rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r)
    onChange(next)
  }

  function removeRow(i: number) {
    onChange(rows.filter((_, idx) => idx !== i))
  }

  return (
    <div className="space-y-1.5">
      {rows.map((row, i) => (
        <div key={i} className="flex gap-1.5 items-center">
          <Input
            placeholder="Header-Name"
            value={row.key}
            onChange={(e) => updateRow(i, 'key', e.target.value)}
            className="h-8 text-xs font-mono flex-1"
          />
          <Input
            placeholder="value"
            value={row.value}
            onChange={(e) => updateRow(i, 'value', e.target.value)}
            className="h-8 text-xs font-mono flex-1"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => removeRow(i)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={addRow}>
        <Plus className="h-3 w-3" />
        {t('apiClient.addHeader')}
      </Button>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────────

interface ApiClientProps {
  defaultUrl?: string
  defaultMethod?: string
  defaultHeaders?: HeaderRow[]
  defaultBody?: string
}

export function ApiClient({ defaultUrl = '', defaultMethod = 'GET', defaultHeaders = [], defaultBody = '' }: ApiClientProps) {
  const [method, setMethod] = useState(defaultMethod)
  const [url, setUrl] = useState(defaultUrl)
  const [headers, setHeaders] = useState<HeaderRow[]>(defaultHeaders)
  const [body, setBody] = useState(defaultBody)
  const [forceShowBody, setForceShowBody] = useState(defaultBody !== '')
  const { t } = useTranslation()

  const { send, isLoading, response, error, history, clearHistory } = useApiClient()

  const showBodyInput = !['GET', 'HEAD', 'OPTIONS'].includes(method) || forceShowBody

  function handleSend() {
    if (!url.trim()) return
    const req: ApiClientRequest = {
      method,
      url: url.trim(),
      headers,
      body: showBodyInput ? body : '',
    }
    void send(req)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSend()
    }
  }

  function formatBody() {
    try {
      const parsed = JSON.parse(body)
      setBody(JSON.stringify(parsed, null, 2))
    } catch {
      // ignore if not valid JSON
    }
  }

  function loadHistory(entry: HistoryEntry) {
    setMethod(entry.request.method)
    setUrl(entry.request.url)
    setHeaders(entry.request.headers)
    setBody(entry.request.body)
    setForceShowBody(entry.request.body !== '')
  }

  return (
    <div className="space-y-4">
      {/* URL bar */}
      <div className="flex gap-2 items-center">
        <Select value={method} onValueChange={(v) => { setMethod(v); setForceShowBody(false) }}>
          <SelectTrigger className="w-28 h-9 text-xs font-mono shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HTTP_METHODS.map((m) => (
              <SelectItem key={m} value={m} className="text-xs font-mono">{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="http://slug.lvh.me:3000/path"
          className="h-9 text-xs font-mono flex-1"
        />

        <HistoryDialog history={history} onSelect={loadHistory} onClear={clearHistory} />

        <Button
          onClick={handleSend}
          disabled={isLoading || !url.trim()}
          className="h-9 gap-1.5 shrink-0"
        >
          <Send className="h-3.5 w-3.5" />
          {isLoading ? t('apiClient.sending') : t('apiClient.send')}
        </Button>
      </div>

      {/* Headers */}
      <div className="space-y-1">
        <button
          type="button"
          className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
        >
          <span>Headers</span>
          {headers.filter(r => r.key).length > 0 && (
            <span className="text-xs font-normal normal-case">({headers.filter(r => r.key).length})</span>
          )}
        </button>
        <HeaderEditor rows={headers} onChange={setHeaders} />
      </div>

      {/* Body */}
      {showBodyInput && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Body</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-5 text-xs px-2 py-0 text-muted-foreground"
              onClick={formatBody}
            >
              {t('apiClient.formatJson')}
            </Button>
          </div>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={'{\n  "key": "value"\n}'}
            className="font-mono text-xs min-h-[100px] resize-y"
          />
        </div>
      )}

      {!showBodyInput && !['GET', 'HEAD', 'OPTIONS'].includes(method) && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1 text-muted-foreground"
          onClick={() => setForceShowBody(true)}
        >
          <ChevronDown className="h-3 w-3" />
          {t('apiClient.addBody')}
        </Button>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Response */}
      {isLoading && (
        <div className="rounded-md border bg-muted/20 p-4 text-sm text-muted-foreground text-center">
          {t('apiClient.sending')}
        </div>
      )}

      {response && !isLoading && (
        <div className="rounded-md border bg-muted/20 p-4">
          <ResponseViewer response={response} />
        </div>
      )}
    </div>
  )
}
